import {areSetsEqual} from '@benchling/js-utils';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {LegacyBowlingLaneConditionREST} from 'procedures/network/ProcedureRunDAO';
import {expandTypedValueForMultiValuePlateMap} from 'procedures/planning/multiValuePlateMap';
import {FullExperimentPlan} from 'procedures/planning/ProcedurePlanTypes';

const MATERIAL_INDEX = 0;
const AMOUNT_INDEX = 1;
const VALUE_INDEX = 0; // parameters and equipment currently only have one field, so we use this index for both

export function serializeExperimentConditionsForNetwork(
  fullExperimentPlan: FullExperimentPlan,
  flowchartMethodIds: ReadonlyArray<string>
): Array<LegacyBowlingLaneConditionREST> {
  const {replicatesPerCondition, namesPerCondition, descriptionsPerCondition, experimentPlanPerCondition} =
    fullExperimentPlan;
  assert(
    replicatesPerCondition.length === experimentPlanPerCondition.length,
    'expected same number of conditions when tracking replicates and planned values'
  );
  assertConditionsCoverFlowchartMethodsExactlyOnce(
    experimentPlanPerCondition,
    namesPerCondition,
    flowchartMethodIds
  );
  return experimentPlanPerCondition.map((methodPlans, i) => ({
    replicateInfos: Array.from({length: replicatesPerCondition[i]}, (_, j) => ({
      replicateNumber: j + 1,
      sourcePMEIIdsByTemplatePMDVId: {},
    })),
    methodPlans: methodPlans.map((methodPlan) => ({
      methodVersionId: methodPlan.methodId,
      dataParameterValues: methodPlan.parameterValues.map((pv) => ({
        dataParameterVersionId: pv.sourceId,
        values: expandTypedValueForMultiValuePlateMap(pv.values[VALUE_INDEX]).map((typedValue) => ({
          cid: `planned_value-${uuidv4()}`,
          value: typedValue,
        })),
      })),
      materialInputValues: methodPlan.materialInputs.map((pv) => ({
        materialInputVersionId: pv.sourceId,
        values: expandTypedValueForMultiValuePlateMap(pv.values[MATERIAL_INDEX]).map(
          (materialTypedValue) => ({
            cid: `planned_value-${uuidv4()}`,
            material: materialTypedValue,
            amount: pv.values[AMOUNT_INDEX] ?? {type: 'ft_float', value: null},
          })
        ),
      })),
      equipmentValues: methodPlan.equipmentInputs.map((pv) => ({
        equipmentVersionId: pv.sourceId,
        values: [{cid: `planned_value-${uuidv4()}`, equipment: pv.values[VALUE_INDEX]}],
      })),
    })),
    name: namesPerCondition[i],
    description: descriptionsPerCondition?.[i] ?? undefined,
  }));
}

/**
 * TODO(BNCH-154822): the backend write path only accepts conditions whose method plans cover
 * every flowchart method exactly once (the bowling-lane shape). Drop once the backend accepts
 * the per-method shape directly.
 */
function assertConditionsCoverFlowchartMethodsExactlyOnce(
  experimentPlanPerCondition: FullExperimentPlan['experimentPlanPerCondition'],
  namesPerCondition: FullExperimentPlan['namesPerCondition'],
  flowchartMethodIds: ReadonlyArray<string>
): void {
  const expectedMethodIds = new Set(flowchartMethodIds);
  experimentPlanPerCondition.forEach((methodPlans, i) => {
    const actualMethodIds = methodPlans.map((p) => p.methodId);
    const actualMethodIdSet = new Set(actualMethodIds);
    assert(
      actualMethodIds.length === actualMethodIdSet.size && areSetsEqual(actualMethodIdSet, expectedMethodIds),
      `Condition "${namesPerCondition[i]}" has method plans ` +
        `[${actualMethodIds.join(', ')}] but the flowchart requires exactly ` +
        `[${[...expectedMethodIds].join(', ')}]`
    );
  });
}
